import CursorIcon from '@/CustomIcons/CursorIcon'
import ExpandIcon from '@/CustomIcons/ExpandIcon'
import PinIcon from '@/CustomIcons/PinIcon'
import { Button } from '@/UIComponents2/Button'
import { cn } from '@/utlis/cn'
import { sessionViewModeState } from './useViewMode'
import CollapseIcon from '@/CustomIcons/CollapseIcon'
import useRemoteDesktopControlPermissionActions from '@/services/useRemoteDesktopControlPermissionActions'
import { useRoomContext } from '@/RoomContext'
import { env } from '@/services/EnvironmentVariables'
import { useMemo } from 'react'
import DesktopIcon from '@/CustomIcons/DesktopIcon'
import { Username } from '@/components/Username'
import { Trans, useTranslation } from 'react-i18next'
import { overlayBgClasses } from '@/components/OverlayControlButton'

type PinnedPresentationOverlayProps = {
    fullscreen: boolean
}

export function PinnedPresentationOverlay(props: PinnedPresentationOverlayProps) {
    const { t } = useTranslation()
    const [roomState] = useRoomContext()

    const {
        ask: desktopControlAsk,
        stop: desktopControlStop,
        screenControlRequestWarning,
        showRemoteDesktopControlQuery,
    } = useRemoteDesktopControlPermissionActions()

    function handleUnpin() {
        sessionViewModeState.exitFullscreen()
        sessionViewModeState.setGalleryLayout({
            name: 'grid',
            page: sessionViewModeState.currentGalleryPage,
            reordering: false,
        })
    }

    const getPeerSessionId = () => {
        if (sessionViewModeState.mode.galleryLayout?.name === 'pinned') {
            const pinnedVideo = sessionViewModeState.mode.galleryLayout.pinnedVideo
            if (pinnedVideo) {
                return pinnedVideo.peerSessionId
            }
        }
    }

    function onRequestScreenControl() {
        showRemoteDesktopControlQuery()
    }

    function onStopScreenControl() {
        const peerSessionId = getPeerSessionId()
        if (peerSessionId) {
            desktopControlStop({ peerSessionId })
        }
    }

    const username = useMemo(() => {
        const peerSessionId = getPeerSessionId()
        if (peerSessionId && roomState.peers[peerSessionId]) {
            const peer = roomState.peers[peerSessionId]
            return peer.displayName ?? peer.username
        }
        return ''
    }, [roomState.peers])

    const position = 'absolute top-2 left-0 right-0 z-10'
    const btnOverride = cn(
        overlayBgClasses,
        'border-muse-toplit-low shadow-glow rounded-[16px] py-[8px] px-[12px] text-[12px] flex gap-1'
    )

    return (
        <div className={cn(position)}>
            <div className="flex gap-2 items-center justify-center group pointer-events-none">
                <div className={cn(btnOverride, 'cursor-default pointer-events-none')}>
                    <DesktopIcon style={{ fontSize: '1.15rem' }} />
                    <Trans i18nKey="<0/>'s Screen" t={t} components={[<Username username={username} />]} />
                </div>
                {env.IS_ELECTRON && (
                    <Button
                        variant="secondary"
                        className={cn(btnOverride, 'pointer-events-auto')}
                        onClick={() => sessionViewModeState.toggleFullscreen()}
                    >
                        {props.fullscreen ? (
                            <>
                                <CollapseIcon style={{ fontSize: '1.15rem' }} /> {t('Exit Fullscreen')}
                            </>
                        ) : (
                            <>
                                <ExpandIcon style={{ fontSize: '1.15rem' }} /> {t('Enter Fullscreen')}
                            </>
                        )}
                    </Button>
                )}
                {roomState.remoteDesktopControlStatus?.status === 'allowed' ? (
                    <Button
                        variant="secondary"
                        className={cn(btnOverride, 'pointer-events-auto')}
                        onClick={onStopScreenControl}
                    >
                        <CursorIcon style={{ fontSize: '1.15rem' }} />
                        {t('Stop Screen Control')}
                    </Button>
                ) : (
                    <Button
                        variant="secondary"
                        className={cn(btnOverride, 'pointer-events-auto')}
                        onClick={onRequestScreenControl}
                    >
                        <CursorIcon style={{ fontSize: '1.15rem' }} />
                        {t('Request Screen Control')}
                    </Button>
                )}
                <Button variant="secondary" className={cn(btnOverride, 'pointer-events-auto')} onClick={handleUnpin}>
                    <PinIcon style={{ fontSize: '1.15rem' }} /> {t('Unpin')}
                </Button>
            </div>
        </div>
    )
}
